import { z } from "zod";
import { startOfDay } from "./dates";
import type { BudgetCategory, VendorType } from "./enums";

/**
 * The payments schedule (iOS `PaymentSchedule` parity): every budget line with
 * a due date plus each vendor's deposit and final balance, bucketed by when
 * they are due. Derived on read from existing columns — no payments table.
 */
export const PAYMENT_SOURCE = ["budget", "vendorDeposit", "vendorBalance"] as const;
export type PaymentSource = (typeof PAYMENT_SOURCE)[number];

export const PAYMENT_BUCKET = ["overdue", "thisMonth", "upcoming", "paid"] as const;
export type PaymentBucket = (typeof PAYMENT_BUCKET)[number];

export function startOfNextMonth(now: Date): Date {
  const d = startOfDay(now);
  d.setDate(1);
  d.setMonth(d.getMonth() + 1);
  return d;
}

/**
 * iOS rules verbatim: paid rows are `paid`; an unpaid row due before today is
 * `overdue` (due today is due, not overdue); before the first of next month is
 * `thisMonth`; anything later is `upcoming`.
 */
export function paymentBucket(due: Date, paid: boolean, now: Date): PaymentBucket {
  if (paid) return "paid";
  if (due.getTime() < startOfDay(now).getTime()) return "overdue";
  if (due.getTime() < startOfNextMonth(now).getTime()) return "thisMonth";
  return "upcoming";
}

export interface PaymentItemDTO {
  /** `<source>:<sourceId>` — stable across reloads, unique across sources. */
  id: string;
  source: PaymentSource;
  /** Budget item id or vendor id. */
  sourceId: string;
  /** Budget line title (category name when empty) or vendor name (vendor type when empty). */
  title: string;
  category: BudgetCategory | null;
  vendorType: VendorType | null;
  /** Linked vendor for budget lines; the vendor itself for deposits and balances. */
  vendorName: string | null;
  dueDate: string;
  /**
   * iOS amounts: a budget line's estimate, a vendor's deposit, a vendor's balance (price − deposit, never negative).
   * `null` when the viewer may not see money.
   */
  amount: number | null;
  /** What is still owed: the estimate minus what was already paid on a budget line; equal to `amount` otherwise. */
  remaining: number | null;
  paid: boolean;
  bucket: PaymentBucket;
}

export interface PaymentTotals {
  /** Sums are `null` without financial access; counts are always there. */
  paid: number | null;
  outstanding: number | null;
  overdue: number | null;
  dueIn30Days: number | null;
  counts: Record<PaymentBucket, number>;
}

export interface PaymentScheduleDTO {
  items: PaymentItemDTO[];
  totals: PaymentTotals;
  generatedAt: string;
}

export const setPaidSchema = z.object({ paid: z.boolean() });
export type SetPaidInput = z.infer<typeof setPaidSchema>;

const DAY_MS = 86_400_000;

/** Bucket counts and sums for a list of payments (server and optimistic client updates share it). */
export function paymentTotals(items: PaymentItemDTO[], now: Date, money: boolean): PaymentTotals {
  const counts: Record<PaymentBucket, number> = { overdue: 0, thisMonth: 0, upcoming: 0, paid: 0 };
  let paid = 0, outstanding = 0, overdue = 0, due30 = 0;
  const horizon = startOfDay(now).getTime() + 31 * DAY_MS;
  for (const p of items) {
    counts[p.bucket] += 1;
    if (p.paid) { paid += p.amount ?? 0; continue; }
    const owed = p.remaining ?? 0;
    outstanding += owed;
    if (p.bucket === "overdue") overdue += owed;
    if (new Date(p.dueDate).getTime() < horizon) due30 += owed;
  }
  return money
    ? { paid, outstanding, overdue, dueIn30Days: due30, counts }
    : { paid: null, outstanding: null, overdue: null, dueIn30Days: null, counts };
}

/** iOS order: open buckets by due date ascending, paid rows most recent first. */
export function sortPayments(items: PaymentItemDTO[]): PaymentItemDTO[] {
  return [...items].sort((a, b) => {
    if (a.paid !== b.paid) return a.paid ? 1 : -1;
    return a.paid ? b.dueDate.localeCompare(a.dueDate) : a.dueDate.localeCompare(b.dueDate);
  });
}
